/**
 * CSV serializers for the controlling reports (BWA, USt-Voranmeldung, Fälligkeitsstaffel,
 * Umsatz nach Quelle). Each reads straight off the computed report object — the same source the
 * screen renders from — so the exported file can never disagree with what is shown. Amounts use
 * the German comma decimal; the ';' separator and UTF-8 BOM are added by the shared csv helpers.
 */
import { csvNumber, toCsv } from './csv';
import type { AgingBucket, BwaReport, VatReport } from './financeAnalytics';
import { invoiceSourceLabels } from './types';

/** BWA statement as a two-column CSV. Signs mirror the screen (Aufwand negativ), so the column sums. */
export function toBwaCsv(bwa: BwaReport): string {
  const rows: string[][] = [
    ['Umsatzerlöse', csvNumber(bwa.revenue)],
    ['Erlösschmälerungen (Gutschriften)', csvNumber(-bwa.creditNotes)],
    ['Gesamtleistung', csvNumber(bwa.netRevenue)],
    ['Wareneinsatz / Fremdleistungen', csvNumber(-bwa.materialExpense)],
    ['Rohertrag', csvNumber(bwa.grossProfit)],
    ...bwa.costBlocks
      .filter((block) => block.key !== 'material')
      .map((block) => [block.label, csvNumber(-block.amount)]),
    ['Zwischenergebnis (vor Personal und Abschreibungen)', csvNumber(bwa.interimResult)],
  ];
  if (bwa.unassignedExpense !== 0) rows.push(['Aufwand ohne BWA-Zuordnung', csvNumber(-bwa.unassignedExpense)]);
  rows.push(['Rohertragsquote (%)', csvNumber(bwa.grossProfitRate)]);
  return toCsv(['Position', 'Betrag (EUR)'], rows);
}

/** Preparatory VAT return: Umsatzsteuer and Vorsteuer split by rate, with totals and Zahllast. */
export function toVatCsv(vat: VatReport): string {
  const rows: string[][] = [];
  for (const row of vat.revenueByRate) rows.push(['Umsatzsteuer', `${row.rate} %`, csvNumber(row.net), csvNumber(row.tax)]);
  rows.push(['Umsatzsteuer gesamt', '', '', csvNumber(vat.vatTotal)]);
  for (const row of vat.inputTaxByRate) rows.push(['Vorsteuer', `${row.rate} %`, csvNumber(row.net), csvNumber(row.tax)]);
  rows.push(['Vorsteuer gesamt', '', '', csvNumber(vat.inputTaxTotal)]);
  rows.push([vat.payable < 0 ? 'Erstattungsanspruch' : 'Verbleibende Zahllast', '', '', csvNumber(Math.abs(vat.payable))]);
  return toCsv(['Bereich', 'Satz', 'Netto (EUR)', 'Steuer (EUR)'], rows);
}

/** Receivables aged into the 30/60/90 buckets, with a closing total row. */
export function toAgingCsv(aging: readonly AgingBucket[]): string {
  const total = aging.reduce((sum, bucket) => sum + bucket.amount, 0);
  const count = aging.reduce((sum, bucket) => sum + bucket.count, 0);
  const rows = aging.map((bucket) => [bucket.label, String(bucket.count), csvNumber(bucket.amount)]);
  rows.push(['Summe offen', String(count), csvNumber(total)]);
  return toCsv(['Altersklasse', 'Belege', 'Offener Betrag (EUR)'], rows);
}

/** Revenue by source (Service, Verkauf, Miete, Vorgänge …). */
export function toRevenueBySourceCsv(bwa: BwaReport): string {
  const rows = bwa.revenueBySource.map((row) => [invoiceSourceLabels[row.source], csvNumber(row.amount), String(row.count)]);
  return toCsv(['Quelle', 'Umsatz (EUR)', 'Rechnungen'], rows);
}
